import { moveInBag, sortBag, splitInBag, type BagRefusalReason, type BagSortOrder, type BagView, type Rules } from "odyssey-shared";

/**
 * The page's reading of a move, a split or a sort before it sends one (P2.8-T14, P2.8-T48): the bag's own rules,
 * `moveInBag`, `splitInBag` and `sortBag`, run on the slots the page holds, so a request the server would refuse is
 * never sent.
 */

/**
 * The number a slot's item stands for when the page's rules lack it: an item no rule stacks with anything.
 */
const UNKNOWN_NUMBER = 0xffff;

/**
 * A slot as the page holds it: its item's rules id or null, and its quantity.
 */
type PageSlot = { readonly item: string | null; readonly quantity: number };

/**
 * Whether the bag's rule would move one slot onto another as the page holds the bag.
 *
 * @param rules The page's rules, which give each item's number and stack.
 * @param slots The slots the page holds, each its item's rules id or null, and its quantity.
 * @param from The slot moved from.
 * @param to The slot moved onto.
 * @param size The bag's size.
 * @returns True when `moveInBag` takes it.
 * @example
 * movable(PAGE_RULES, [{ item: "minor_healing_potion", quantity: 5 }], 0, 3, 20);
 * // true
 */
export function movable(rules: Rules, slots: readonly PageSlot[], from: number, to: number, size: number): boolean {
  const moving = slots[from]?.item ?? null;
  const stack = moving === null ? 1 : (rules.items.get(moving)?.stack ?? 1);
  return moveInBag(viewOf(rules, slots), from, to, stack, size).ok;
}

/**
 * Where the bag's rule would put the new half of a split of a slot as the page holds the bag (the author's controls of
 * 2026-10-08): the first empty slot inside the size, or null for a split it refuses (a stack of 1, an empty slot, no
 * empty slot, a slot past the bag).
 *
 * @param rules The page's rules.
 * @param slots The slots the page holds.
 * @param slot The slot split.
 * @param size The bag's size.
 * @returns The slot the new half lands in, or null.
 * @example
 * splitTarget(PAGE_RULES, [{ item: "minor_healing_potion", quantity: 20 }], 0, 20);
 * // 1
 */
export function splitTarget(rules: Rules, slots: readonly PageSlot[], slot: number, size: number): number | null {
  const split = splitInBag(viewOf(rules, slots), slot, size);
  return split.ok ? (split.slots[1] as number) : null;
}

/**
 * Why the bag's rule would refuse a split of a slot as the page holds the bag, for the item menu's reason under Split
 * stack (ADR-125 decision 7.2): `empty` for a slot holding fewer than 2 units, `full` for a bag with no empty slot,
 * `range` for a slot past the bag; null when it would split.
 *
 * @param rules The page's rules.
 * @param slots The slots the page holds.
 * @param slot The slot split.
 * @param size The bag's size.
 * @returns The rule's reason, or null.
 * @example
 * splitRefusalOf(PAGE_RULES, [{ item: "minor_healing_potion", quantity: 1 }], 0, 20);
 * // "empty"
 */
export function splitRefusalOf(rules: Rules, slots: readonly PageSlot[], slot: number, size: number): BagRefusalReason | null {
  const split = splitInBag(viewOf(rules, slots), slot, size);
  return split.ok ? null : split.reason;
}

/**
 * Whether sorting the bag in an order would change it as the page holds it (ADR-125 decision 3.6): a bag already in the
 * order sends nothing. Newest first reads times only the map server holds (decision 3.8), so the page asks whenever
 * the bag has anything to put in an order: a stack to pack to the front, or two stacks.
 *
 * @param rules The page's rules, whose items the sort reads by number.
 * @param slots The slots the page holds.
 * @param by The order.
 * @param size The bag's size.
 * @param kept The slots whose stack is kept in place, which the sort leaves where they are (ADR-125 decision 6.3); none
 *   unless given.
 * @returns True when `sortBag` changes a slot.
 * @example
 * sortChanges(PAGE_RULES, [{ item: null, quantity: 0 }, { item: "minor_healing_potion", quantity: 5 }], "name", 20);
 * // true
 */
export function sortChanges(rules: Rules, slots: readonly PageSlot[], by: BagSortOrder, size: number, kept: readonly number[] = []): boolean {
  const items = rules.tables.items;
  const flags = slots.map((_, slot) => (kept.includes(slot) ? 1 : 0));
  const order = by === "newest" ? "name" : by;
  const changes = sortBag(viewOf(rules, slots), order, (item) => items[item - 1], size, flags).slots.length > 0;
  return changes || (by === "newest" && slots.filter((slot) => slot.item !== null).length >= 2);
}

/**
 * The page's slots as the bag's rules read a bag.
 *
 * @param rules The page's rules.
 * @param slots The slots the page holds.
 * @returns The two arrays.
 */
function viewOf(rules: Rules, slots: readonly PageSlot[]): BagView {
  const items = new Uint16Array(slots.length);
  const quantities = new Uint16Array(slots.length);
  slots.forEach((slot, index) => {
    items[index] = slot.item === null ? 0 : numberOf(rules, slot.item);
    quantities[index] = slot.quantity;
  });
  return { items, quantities };
}

/**
 * An item's number as the bag's rule reads it: its place in the rules' items table from 1, or a number no rule stacks
 * with anything for an id the page's rules lack.
 *
 * @param rules The page's rules.
 * @param item The item's rules id.
 * @returns The number.
 */
function numberOf(rules: Rules, item: string): number {
  const index = rules.tables.items.findIndex((each) => each.id === item);
  return index < 0 ? UNKNOWN_NUMBER : index + 1;
}
